import { useEffect, useState } from 'react'
import { useEditor } from '../store/useEditor'
import { Empty, Loading, SaidLine, Sheet, useSaid } from './bits'

/**
 * THE PICTURE, IMMEDIATELY.
 *
 * Most people who open this app never write a quote. They photograph a house,
 * draw the lights, and text the homeowner the picture from the driveway. That
 * is the whole product for them, and the app used to make them pick their way
 * to it: a button called Export beside a button called Quote, and a menu item
 * called Send picture three levels down.
 *
 * So Share design renders the image and shows it. No question first. Reported
 * in the design package as the one change that matters most in the editor:
 * avoid an extra mandatory "mockup only or mockup and a quote?" choice before
 * showing anybody the image.
 *
 * THE QUOTE IS A BRANCH OFF THE PICTURE, not a gate in front of it. It sits
 * under the image as a quieter second action, so the person who does price
 * this house finds it exactly where they are already looking.
 *
 * NOTHING HERE PROVES ANYTHING WAS DELIVERED. A share sheet opening is not a
 * message sent and a download is not a customer who has seen it, so this never
 * marks a job as sent. That is a decision a person makes on the job itself.
 */

export default function ShareSheet({
  render,
  fileName,
  canShare,
  onQuote,
  onClose,
}: {
  /** Produces the finished image. The stage owns the renderer; this does not. */
  render: () => Promise<Blob | null>
  fileName: string
  canShare: boolean
  onQuote: () => void
  onClose: () => void
}) {
  const name = useEditor((s) => s.doc.name)
  const customer = useEditor((s) => s.doc.customer)
  const [url, setUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [failed, setFailed] = useState(false)
  const { said, say } = useSaid()

  /*
   * Rendered once, when this opens. A second render on every press would cost
   * a full canvas readback to hand back the same pixels.
   */
  useEffect(() => {
    let alive = true
    let made: string | null = null
    render()
      .then((b) => {
        if (!alive) return
        if (!b) {
          setFailed(true)
          return
        }
        made = URL.createObjectURL(b)
        setBlob(b)
        setUrl(made)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
      // The object URL holds the whole JPEG in memory until it is released.
      if (made) URL.revokeObjectURL(made)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const download = () => {
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    say('Saved to your downloads.')
  }

  const send = async () => {
    if (!blob) return
    try {
      const file = new File([blob], fileName, { type: 'image/jpeg' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: customer || name })
      } else {
        download()
      }
    } catch {
      /* the person closed the share sheet, which is not a failure */
    }
  }

  return (
    <Sheet
      title={customer || name}
      variant="share-sheet"
      onClose={onClose}
      foot={
        <>
          <SaidLine said={said} />
          {/*
            THE ORDER IS THE ORDER EVERY OTHER SHEET USES: the quiet way out
            on the left, the primary last. This screen used to put Send first
            and argue that a phone in a driveway wants it first. It does want
            it first - and last is first on a phone, because the actions stack
            and the bottom of a stack is the end of the thumb. The exception
            was costing the product its one dialog convention to arrive at the
            same button in the same place.
          */}
          <button className="btn ghost" onClick={onQuote}>
            Include a quote
          </button>
          {canShare && (
            <button className="btn" onClick={download} disabled={!url}>
              Download image
            </button>
          )}
          {canShare ? (
            <button className="btn primary" onClick={() => void send()} disabled={!blob}>
              Send this picture
            </button>
          ) : (
            <button className="btn primary" onClick={download} disabled={!url}>
              Download image
            </button>
          )}
        </>
      }
    >
      <div className="share-frame">
        {url ? (
          <img src={url} alt="The finished design" />
        ) : failed ? (
          <Empty kind="wrong">That image could not be built. Close this and try again.</Empty>
        ) : (
          <Loading what="Rendering the picture" />
        )}
      </div>
    </Sheet>
  )
}

